import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Navigate } from "react-router-dom";
import { Layers, Locate, Navigation2, Camera, ExternalLink } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import "@/webapp/fleet-owners/fleet.css";
import DriverMap, { KINDS, USES, Legend, mapCredit } from "./DriverMap";
import { SpotDetail, NextStep, NavSummary, StepList } from "./Directions";
import { useNavigation } from "./useNavigation";
import { useDesktop, useStored, useCompass, askCompass, useInstall, useFavorites, usePlaceSearch } from "./useDevice";
import { useVoice } from "./useVoice";
import { BottomSheet, TabBar } from "./Sheet";
import SearchBar from "./SearchBar";
import LiveView from "./LiveView";
import Planner from "./Planner";
import { useAlong, AlongList } from "./Along";
import { Controls, SpotCard, SavedList, ProfilePanel } from "./Panels";

const REFRESH_MS = 120000;
const TABBAR_H = "calc(56px + env(safe-area-inset-bottom))";
const distKm = (a, b) => {
  const R = 6371, rad = Math.PI / 180, dLat = (b.lat - a.lat) * rad, dLon = (b.lon - a.lon) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(h)) * 10) / 10;
};

function useWakeLock(on) {
  useEffect(() => {
    if (!on || !navigator.wakeLock) return undefined;
    let lock = null, dead = false;
    const get = () => navigator.wakeLock.request("screen").then((l) => { if (dead) l.release(); else lock = l; }).catch(() => {});
    get();
    const vis = () => { if (document.visibilityState === "visible") get(); };
    document.addEventListener("visibilitychange", vis);
    return () => { dead = true; document.removeEventListener("visibilitychange", vis); lock?.release().catch(() => {}); };
  }, [on]);
}

function Fab({ onClick, active, label, testid, children }) {
  return <button data-testid={testid} onClick={onClick} aria-label={label} title={label} className={`flex h-11 w-11 items-center justify-center rounded-full shadow-[0_3px_12px_rgba(10,35,74,0.25)] transition-transform active:scale-95 ${active ? "bg-[#0A234A] text-white" : "bg-white text-[#0A234A]"}`}>{children}</button>;
}

export default function DriverFinder() {
  const { user, logout } = useAuth();
  const [loc, setLoc] = useState(null);
  const [radius, setRadius] = useState(50);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [picked, setPicked] = useState(null);
  const [kinds, setKinds] = useState(Object.fromEntries(Object.keys(KINDS).map((k) => [k, true])));
  const [follow, setFollow] = useState(true);
  const [use, setUse] = useState("all");
  const [tab, setTab] = useState("explore");
  const [snap, setSnap] = useState("half");
  const [live, setLive] = useState(false);
  const [compassOn, setCompassOn] = useState(false);
  const [recenter, setRecenter] = useState(0);
  const [voice, setVoice] = useStored("fp.drv.voice", true);
  const [satellite, setSatellite] = useStored("fp.drv.satellite", false);
  const [headingUp, setHeadingUp] = useStored("fp.drv.headingup", true);
  const [plan, setPlan] = useState({ from: "me", to: null, stops: [], key: 0 });
  const [planning, setPlanning] = useState(false);
  const [navTab, setNavTab] = useState("turns");
  const [corridor, setCorridor] = useState(5);
  const allowed = user && ["driver", "admin"].includes(user.role);
  const seq = useRef(0);
  const desktop = useDesktop();
  const baseOrigin = data?.origin || (loc && { lat: loc.lat, lon: loc.lon });
  const nav = useNavigation(baseOrigin);
  const me = nav.pos || baseOrigin;
  const compass = useCompass(compassOn);
  const favs = useFavorites(allowed);
  const search = usePlaceSearch(me);
  const install = useInstall();
  const speak = useVoice(nav, voice && !nav.preview);
  const along = useAlong(nav, !!nav.dest, corridor);
  useWakeLock(!!nav.dest);

  const { track } = nav;
  const onGps = useCallback((p) => {
    setLoc({ method: "gps", lat: p.coords.latitude, lon: p.coords.longitude, accuracy_km: p.coords.accuracy / 1000 });
    track();
  }, [track]);

  useEffect(() => {
    if (!allowed) return;
    api.get("/driver/locate").then(({ data: d }) => { if (d.located) setLoc((p) => (p?.method === "gps" ? p : d)); else setErr(d.detail); }).catch((e) => setErr(errorText(e, "Could not locate you.")));
    navigator.geolocation?.getCurrentPosition(onGps, () => {}, { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 });
  }, [allowed, onGps]);

  const runSearch = useCallback(async () => {
    if (!loc) return;
    const id = ++seq.current;
    setBusy(true);
    try {
      const { data: d } = await api.get("/driver/nearby", { params: { lat: loc.lat, lon: loc.lon, radius_km: radius } });
      if (id === seq.current) { setData(d); setErr(""); }
    } catch (e) { if (id === seq.current) setErr(errorText(e, "Search failed.")); }
    if (id === seq.current) setBusy(false);
  }, [loc, radius]);
  useEffect(() => { runSearch(); const t = setInterval(runSearch, REFRESH_MS); return () => clearInterval(t); }, [runSearch]);

  const pick = useCallback((r) => {
    setPicked({ ...r, distance_km: me ? distKm(me, r) : r.distance_km ?? null });
    setTab("explore");
    setSnap("half");
  }, [me]);
  const go = useCallback((r) => {
    setPicked(null);
    setPlanning(false);
    setNavTab("turns");
    setFollow(true);
    setTab("explore");
    setSnap("peek");
    nav.start(r);
    if (headingUp) askCompass().then(setCompassOn);
  }, [nav, headingUp]);
  const openPlanner = useCallback((to) => {
    setPlan((p) => ({ from: "me", to: to || null, stops: [], key: p.key + 1 }));
    setPlanning(true);
    setPicked(null);
    setTab("explore");
    setSnap("full");
  }, []);
  const goPlan = useCallback(() => {
    const from = plan.from === "me" ? null : plan.from;
    setPlanning(false);
    setPicked(null);
    setNavTab("turns");
    setTab("explore");
    setFollow(!from);
    setSnap(from ? "half" : "peek");
    nav.start(plan.to, { via: plan.stops.filter(Boolean), from });
    if (!from && headingUp) askCompass().then(setCompassOn);
  }, [nav, plan, headingUp]);
  const endNav = useCallback(() => {
    if (nav.pos) setLoc({ method: "gps", lat: nav.pos.lat, lon: nav.pos.lon, accuracy_km: (nav.pos.acc || 20) / 1000 });
    setLive(false);
    setSnap("half");
    nav.stop();
  }, [nav]);
  const openLive = useCallback(() => { askCompass().then(setCompassOn); setLive(true); }, []);

  const gps = () => {
    if (!navigator.geolocation) { setErr("GPS is not available in this browser."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { onGps(p); setLocating(false); setRecenter((n) => n + 1); },
      (e) => { setErr(e.code === 1 ? "Location permission denied. Using IP location." : "Could not get GPS fix."); setLocating(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  };

  const counts = useMemo(() => {
    const all = data?.results || [];
    return { all: all.length, park: all.filter((r) => USES[r.kind]?.includes("park")).length, rest: all.filter((r) => USES[r.kind]?.includes("rest")).length };
  }, [data]);
  const results = useMemo(() => (data?.results || []).filter((r) => (kinds[r.kind] ?? true) && (use === "all" || USES[r.kind]?.includes(use))), [data, kinds, use]);
  if (user === undefined) return null;
  if (!allowed) return <Navigate to="/dashboard" replace />;

  const navOn = !!nav.dest;
  const step = nav.route && nav.progress ? nav.route.steps[nav.progress.next] : null;
  const moving = (nav.pos?.speed || 0) > 2 && nav.pos?.heading != null;
  const mapBearing = navOn && headingUp && follow && nav.pos ? (moving ? nav.pos.heading : compass) : null;
  const kmh = navOn && nav.pos?.speed != null ? Math.round(nav.pos.speed * 3.6) : null;
  const liveCount = results.filter((r) => r.available != null).length;
  const prefs = { voice, setVoice, satellite, setSatellite, headingUp, setHeadingUp };

  const explore = planning ? (
    <Planner plan={plan} setPlan={setPlan} near={me} spots={data?.results || []} favorites={favs.list} onClose={() => { setPlanning(false); setSnap("half"); }} onGo={goPlan} busy={nav.busy} />
  ) : picked ? (
    <SpotDetail r={picked} origin={me} saved={favs.ids.has(picked.id)} onSave={() => favs.toggle(picked)} onClose={() => setPicked(null)} onDirections={() => go(picked)}
      onPlan={navOn ? null : () => openPlanner(picked)} onAddStop={navOn ? () => { nav.addStop(picked); setPicked(null); } : null} stopAdded={nav.via.some((v) => v.id === picked.id)} />
  ) : (
    <>
      <Controls loc={loc} radius={radius} setRadius={setRadius} gps={gps} locating={locating} refresh={runSearch} busy={busy} kinds={kinds} setKinds={setKinds} use={use} setUse={setUse} counts={counts} />
      {err && <p className="border-b border-red-100 bg-red-50 px-4 py-2 text-xs font-semibold text-red-600" data-testid="drv-error">{err}</p>}
      <p className="px-4 py-2 text-xs text-slate-500" data-testid="drv-count">{data ? `${results.length} spots within ${data.radius_km} km · ${liveCount} with live availability` : "Searching…"}</p>
      <ul data-testid="drv-list">
        {data && results.map((r) => <SpotCard key={r.id} r={r} saved={favs.ids.has(r.id)} onPick={pick} onGo={go} />)}
        {data && !results.length && <li className="px-4 py-10 text-center text-sm text-slate-400" data-testid="drv-empty">No truck parking or rest areas found in this radius. Try a wider search.</li>}
      </ul>
      {data && (
        <footer className="border-t border-slate-100 px-4 py-2 text-[10px] leading-relaxed text-slate-400" data-testid="drv-sources">
          {data.sources.map((s) => <span key={s.id} className="mr-2">{s.ok ? "●" : "○"} {s.id.replace("_", " ")} {s.ok ? s.count : "offline"}</span>)}
          <br />{data.attribution}
        </footer>
      )}
    </>
  );
  const panel = tab === "saved" ? <SavedList favs={favs} onPick={pick} onGo={go} />
    : tab === "profile" ? <ProfilePanel user={user} logout={logout} prefs={prefs} install={install} onVoiceTest={() => speak("Spoken directions are on.", true)} />
    : explore;
  const searchBar = <SearchBar search={search} spots={data?.results || []} favorites={favs.list} onPick={pick} onPlan={() => openPlanner(null)} />;
  const navBody = picked ? explore : (
    <>
      <div className="grid grid-cols-2 gap-1 px-4 pb-2" data-testid="drv-nav-tabs">
        {[["turns", "Turns"], ["stops", `Stops along route${along.results.length ? ` (${along.results.length})` : ""}`]].map(([k, label]) => (
          <button key={k} data-testid={`drv-nav-tab-${k}`} onClick={() => { setNavTab(k); if (k === "stops" && snap === "peek") setSnap("half"); }} className={`rounded-full py-1.5 text-xs font-semibold transition-colors ${navTab === k ? "bg-[#0A234A] text-white" : "bg-slate-100 text-slate-600"}`}>{label}</button>
        ))}
      </div>
      {navTab === "stops" ? <AlongList along={along} nav={nav} corridor={corridor} setCorridor={setCorridor} onPick={pick} /> : <StepList nav={nav} follow={follow} setFollow={setFollow} />}
    </>
  );

  return (
    <div className="fo-shell fixed inset-0 flex overflow-hidden bg-white text-[#0A234A] lg:static lg:h-[100dvh]" data-testid="driver-finder">
      {desktop && (
        <aside className="flex w-[420px] flex-shrink-0 flex-col border-r border-slate-200" data-testid="drv-side-panel">
          <header className="flex h-14 flex-shrink-0 items-center gap-3 bg-[#0A234A] px-4 text-white">
            <span className="font-display text-lg font-bold">FLEET<span className="text-[#FF5722]">PODS</span></span>
            <span className="font-mono text-[10px] tracking-[0.2em] text-white/50">DRIVER</span>
            <span className="ml-auto truncate font-mono text-xs text-white/60">{user.email}</span>
          </header>
          {navOn ? (
            <>
              <div className="pt-3"><NavSummary nav={nav} onEnd={endNav} voice={voice} setVoice={setVoice} onLive={openLive} /></div>
              {step && <NextStep step={step} dist={nav.progress.to_next_m} />}
              <div className="min-h-0 flex-1 overflow-y-auto">{navBody}</div>
            </>
          ) : (
            <>
              <div className="p-3">{searchBar}</div>
              <TabBar desktop tab={tab} setTab={setTab} savedCount={favs.list.length} />
              <div className="min-h-0 flex-1 overflow-y-auto pt-3">{panel}</div>
            </>
          )}
        </aside>
      )}
      <main className="relative min-w-0 flex-1" data-testid="drv-map">
        {baseOrigin ? <DriverMap origin={baseOrigin} radius={radius} results={navOn ? along.results : results} focus={picked} onPick={pick} nav={nav} follow={follow && !!nav.pos && !!nav.route} satellite={satellite} bearing={mapBearing} recenter={recenter} attribution={desktop} padBottom={desktop ? 0 : Math.round(window.innerHeight * 0.52 + 56)} />
          : <div className="flex h-full items-center justify-center text-sm text-slate-400" data-testid="drv-map-loading">Finding your location…</div>}
        {desktop && baseOrigin && !navOn && !picked && <Legend />}
        {!desktop && (
          <div className="absolute inset-x-3 top-[max(12px,env(safe-area-inset-top))] z-[750]">
            {navOn ? (step ? <NextStep step={step} dist={nav.progress.to_next_m} className="rounded-2xl shadow-xl" /> : <div className="rounded-2xl bg-[#0A234A] px-4 py-3 text-sm font-semibold text-white shadow-xl" data-testid="drv-next-step-loading">Calculating route…</div>) : searchBar}
          </div>
        )}
        {(desktop || snap !== "full") && <div className={`absolute right-3 z-[740] flex flex-col gap-2.5 ${desktop ? "top-3" : navOn ? "top-[calc(max(12px,env(safe-area-inset-top))+96px)]" : "top-[calc(max(12px,env(safe-area-inset-top))+64px)]"}`} data-testid="drv-fabs">
          <Fab testid="drv-fab-layers" label={satellite ? "Street map" : "Satellite map"} active={satellite} onClick={() => setSatellite(!satellite)}><Layers className="h-5 w-5" /></Fab>
          {navOn && <Fab testid="drv-fab-heading" label={headingUp ? "North up" : "Heading up"} active={headingUp} onClick={() => { if (!headingUp) askCompass().then(setCompassOn); setHeadingUp(!headingUp); }}><Navigation2 className="h-5 w-5 transition-transform" style={{ transform: `rotate(${mapBearing != null ? -mapBearing : 0}deg)` }} /></Fab>}
          <Fab testid="drv-fab-locate" label="My location" active={navOn && follow} onClick={() => { if (navOn) setFollow(true); else if (nav.pos) setRecenter((n) => n + 1); else gps(); }}><Locate className="h-5 w-5" /></Fab>
          {navOn && !nav.preview && <Fab testid="drv-fab-live" label="Live camera view" onClick={openLive}><Camera className="h-5 w-5" /></Fab>}
        </div>}
        {kmh != null && <div className={`absolute left-3 z-[740] flex h-14 w-14 flex-col items-center justify-center rounded-full border-[3px] border-[#0A234A] bg-white font-mono leading-none shadow-lg ${desktop ? "bottom-6" : "top-[calc(max(12px,env(safe-area-inset-top))+96px)]"}`} data-testid="drv-speed"><b className="text-lg text-[#0A234A]">{kmh}</b><span className="text-[9px] text-slate-500">km/h</span></div>}
        {desktop && data && !navOn && <a href="https://www.openstreetmap.org/fixthemap" target="_blank" rel="noreferrer" className="absolute bottom-3 right-3 z-[500] inline-flex items-center gap-1 rounded-md bg-white/95 px-2 py-1 text-[10px] font-semibold text-slate-500 shadow" data-testid="drv-missing-spot"><ExternalLink className="h-3 w-3" />Missing a spot?</a>}
        {!desktop && (
          <BottomSheet snap={snap} setSnap={setSnap} bottom={navOn ? 0 : TABBAR_H}
            header={<>{navOn && <NavSummary nav={nav} onEnd={endNav} voice={voice} setVoice={setVoice} onLive={openLive} />}<p className="pointer-events-none absolute right-3 top-1.5 text-[8px] text-slate-400" data-testid="drv-map-credit">{mapCredit(satellite)}</p></>}>
            {navOn ? navBody : panel}
          </BottomSheet>
        )}
        {!desktop && !navOn && <TabBar tab={tab} setTab={(t) => { setTab(t); if (snap === "peek") setSnap("half"); }} savedCount={favs.list.length} />}
      </main>
      {live && navOn && <LiveView nav={nav} compass={compass} onClose={() => setLive(false)} />}
    </div>
  );
}
